/* ──────────────────────────────────────────────────────────────
   Distribution/Webhooks/index.tsx · Webhooks (live — /api/v1/webhooks)
   ────────────────────────────────────────────────────────────── */

import React, { useState, useEffect, useCallback } from 'react';
import { useReloadOnFocus } from '../../navigation/useReloadOnFocus';
import {
  View,
  Text,
  ScrollView,
  ActivityIndicator,
  ViewStyle,
} from 'react-native';

import { useTheme } from '../../context/themeContext';
import {
  Icon,
  Button,
  Card,
  Chip,
  Input,
  Checkbox,
  Modal,
  Empty,
  Popover,
  MenuItem,
  MenuSeparator,
  ConfirmDialog,
  useToast,
} from '../../components/ui';
import {
  getWebhooks,
  createWebhook,
  updateWebhook,
  deleteWebhook,
  rotateWebhookSecret,
  getDeliveries,
  retryDelivery,
} from '../../services/catalog/webhook';
import { handleApiError } from '../../utils/errorHandler';
import { showSuccess } from '../../utils/successHandler';
import { WEBHOOK_EVENTS, type Webhook } from '../../schemas/catalog/webhook';

const EVENTS = WEBHOOK_EVENTS;

// ── WebhookCard ───────────────────────────────────────────────

function WebhookCard({ webhook: w, onToggle, onEdit, onDelete, onRotate, onLog }: {
  webhook: Webhook;
  onToggle: (w: Webhook) => void;
  onEdit: (w: Webhook) => void;
  onDelete: (w: Webhook) => void;
  onRotate: (w: Webhook) => void;
  onLog: (w: Webhook) => void;
}) {
  const { theme } = useTheme();
  const { colors, fontSize, fontWeight } = theme;
  const isActive = w.status === 'ACTIVE';

  return (
    <Card style={{ marginBottom: 0 }}>
      <View style={{ flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
        <View style={{ flex: 1, minWidth: 0 }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <Icon name="webhook" size={16} color={colors.ink3} />
            <Text numberOfLines={1} style={{ flexShrink: 1, fontFamily: theme.font.mono, fontSize: fontSize.md, color: colors.ink, fontWeight: fontWeight.medium }}>
              {w.url}
            </Text>
            <Chip tone={isActive ? 'success' : 'neutral'} dot>{(w.status || '').toLowerCase()}</Chip>
          </View>
          {!!w.description && (
            <Text style={{ fontSize: fontSize.sm, color: colors.ink3, marginTop: 4, fontFamily: theme.font.sans }}>{w.description}</Text>
          )}
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 5, marginTop: 10 }}>
            {(w.eventTypes || []).map((e) => (
              <Chip key={e} tone={e === '*' ? 'info' : 'outline'}>
                <Text style={{ fontFamily: e === '*' ? theme.font.sans : theme.font.mono, fontSize: fontSize.xs, color: e === '*' ? colors.info : colors.ink2 }}>
                  {e === '*' ? 'All events — every change is sent' : e}
                </Text>
              </Chip>
            ))}
          </View>
        </View>

        <View style={{ alignItems: 'flex-end' }}>
          <Popover align="right" width={170} trigger={<Button size="sm" icon="more" iconOnly />}>
            {(close: () => void) => (
              <>
                <MenuItem icon="edit" onPress={() => { close(); onEdit(w); }}>Edit</MenuItem>
                <MenuItem icon={isActive ? 'pause' : 'play'} onPress={() => { close(); onToggle(w); }}>
                  {isActive ? 'Pause' : 'Resume'}
                </MenuItem>
                <MenuItem icon="history" onPress={() => { close(); onLog(w); }}>Delivery log</MenuItem>
                <MenuItem icon="refresh" onPress={() => { close(); onRotate(w); }}>Rotate secret</MenuItem>
                <MenuSeparator />
                <MenuItem icon="trash" danger onPress={() => { close(); onDelete(w); }}>Remove</MenuItem>
              </>
            )}
          </Popover>
        </View>
      </View>
    </Card>
  );
}

// ── AddWebhookModal ───────────────────────────────────────────

function WebhookFormModal({ open, onClose, onSubmit, busy, title, submitLabel, busyLabel, initial }: {
  open: boolean;
  onClose: () => void;
  onSubmit: (url: string, eventTypes: string[], description?: string) => void;
  busy: boolean;
  title: string;
  submitLabel: string;
  busyLabel: string;
  initial?: { url?: string; description?: string; eventTypes?: string[] };
}) {
  const { theme } = useTheme();
  const { colors, fontSize, fontWeight } = theme;
  const [url, setUrl] = useState('');
  const [description, setDescription] = useState('');
  const [checked, setChecked] = useState<Record<string, boolean>>({ ITEM_UPSERT: true });

  // Seed the form each time the modal opens — from `initial` (edit) or blank (add).
  useEffect(() => {
    if (!open) return;
    const evs = initial?.eventTypes ?? [];
    let next: Record<string, boolean> = {};
    if (evs.includes('*')) {
      EVENTS.forEach(e => { next[e.value] = true; });
    } else if (evs.length) {
      evs.forEach(v => { next[v] = true; });
      next['*'] = EVENTS.filter(e => e.value !== '*').every(e => next[e.value]);
    } else {
      next = { ITEM_UPSERT: true };
    }
    setUrl(initial?.url ?? '');
    setDescription(initial?.description ?? '');
    setChecked(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // When "All events" (*) is on, the subscription is simply ['*']; otherwise
  // send the individually-checked events.
  const selected = checked['*']
    ? ['*']
    : EVENTS.filter(e => e.value !== '*' && checked[e.value]).map(e => e.value);

  // We warn (but don't block) when the endpoint isn't https — http / a bare
  // domain sends payloads unencrypted. The URL is sent exactly as typed.
  const trimmed = url.trim();
  const notSecure = trimmed !== '' && !/^https:\/\//i.test(trimmed);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      footer={
        <>
          <Button onPress={onClose}>Cancel</Button>
          <Button
            kind="accent"
            icon="webhook"
            disabled={!trimmed || selected.length === 0 || busy}
            onPress={() => onSubmit(trimmed, selected, description.trim() || undefined)}
          >
            {busy ? busyLabel : submitLabel}
          </Button>
        </>
      }
    >
      <View style={{ gap: 16 }}>
        <View>
          <Input label="Payload URL" placeholder="https://…" value={url} onChangeText={setUrl} inputStyle={{ fontFamily: theme.font.mono }} autoFocus />
          {notSecure && (
            <Text style={{ fontSize: fontSize.xs, color: colors.danger, fontFamily: theme.font.sans, marginTop: 6 }}>
              This URL isn't https:// — your catalog data would be sent unencrypted, a serious security risk. We recommend https://.
            </Text>
          )}
        </View>
        <Input label="Description" placeholder="Optional" value={description} onChangeText={setDescription} />
        <View>
          <Text style={{ fontSize: fontSize.sm, fontWeight: fontWeight.medium, color: colors.ink2, marginBottom: 8, fontFamily: theme.font.sans }}>Events</Text>
          <View style={{ gap: 6 }}>
            {EVENTS.map((e) => (
              <Checkbox key={e.value} label={e.label} checked={!!checked[e.value]} onChange={(val) => setChecked(prev => {
                // "All events" master toggles every checkbox together.
                if (e.value === '*') {
                  const next: Record<string, boolean> = {};
                  EVENTS.forEach(ev => { next[ev.value] = val; });
                  return next;
                }
                // Keep the master in sync: it's on only when every event is checked.
                const next = { ...prev, [e.value]: val };
                const individuals = EVENTS.filter(ev => ev.value !== '*');
                next['*'] = individuals.every(ev => next[ev.value]);
                return next;
              })} />
            ))}
          </View>
        </View>
      </View>
    </Modal>
  );
}

// ── Main screen ───────────────────────────────────────────────

export default function WebhooksScreen() {
  const { theme } = useTheme();
  const { colors, fontSize, fontWeight } = theme;

  const [hooks, setHooks] = useState<Webhook[]>([]);
  const [loading, setLoading] = useState(true);
  const [newOpen, setNewOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [editTarget, setEditTarget] = useState<Webhook | null>(null);
  const [editBusy, setEditBusy] = useState(false);
  const [delTarget, setDelTarget] = useState<Webhook | null>(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getWebhooks();
      setHooks(res.items ?? []);
    } catch (err) {
      handleApiError(err, 'Could not load webhooks');
    } finally {
      setLoading(false);
    }
  }, []);

  useReloadOnFocus(load);

  const onCreate = async (url: string, eventTypes: string[], description?: string) => {
    setBusy(true);
    try {
      const res = await createWebhook({ url, eventTypes, ...(description ? { description } : {}) });
      showSuccess(res, 'Webhook added', 'Endpoint subscribed.');
      setNewOpen(false);
      await load();
    } catch (err) {
      // Modal stays open so the user can fix the input; the toast renders above it.
      handleApiError(err, 'Could not add webhook', 'Invalid request. Check the URL and selected events.');
    } finally {
      setBusy(false);
    }
  };

  const onEditSubmit = async (url: string, eventTypes: string[], description?: string) => {
    if (!editTarget) return;
    setEditBusy(true);
    try {
      await updateWebhook(editTarget.id, { url, eventTypes, description: description ?? '' });
      showSuccess({}, 'Webhook updated', 'Changes saved.');
      setEditTarget(null);
      await load();
    } catch (err) {
      // Modal stays open so the user can fix the input; the toast renders above it.
      handleApiError(err, 'Could not update webhook', 'Invalid request. Check the URL and selected events.');
    } finally {
      setEditBusy(false);
    }
  };

  const onToggle = async (w: Webhook) => {
    try {
      const next = w.status === 'ACTIVE' ? 'SUSPENDED' : 'ACTIVE';
      await updateWebhook(w.id, { status: next });
      showSuccess({}, next === 'ACTIVE' ? 'Resumed' : 'Paused', `Webhook ${next.toLowerCase()}.`);
      await load();
    } catch (err) {
      handleApiError(err, 'Could not update webhook');
    }
  };

  const onDelete = (w: Webhook) => setDelTarget(w);

  const confirmDelete = async () => {
    if (!delTarget) return;
    setDeleting(true);
    try {
      await deleteWebhook(delTarget.id);
      showSuccess({}, 'Webhook removed', 'Endpoint unsubscribed.');
      setHooks(prev => prev.filter(x => x.id !== delTarget.id));
      setDelTarget(null);
    } catch (err) {
      handleApiError(err, 'Could not remove webhook');
    } finally {
      setDeleting(false);
    }
  };

  const onRotate = async (w: Webhook) => {
    try {
      await rotateWebhookSecret(w.id);
      showSuccess({}, 'Secret rotated', 'A new signing secret was issued.');
      await load();
    } catch (err) {
      handleApiError(err, 'Could not rotate secret');
    }
  };

  // Delivery log + manual retry
  const [logFor, setLogFor] = useState<Webhook | null>(null);
  const [deliveries, setDeliveries] = useState<any[]>([]);
  const [logLoading, setLogLoading] = useState(false);

  const openLog = async (w: Webhook) => {
    setLogFor(w); setLogLoading(true); setDeliveries([]);
    try {
      const res = await getDeliveries(w.id);
      setDeliveries(res.items ?? []);
    } catch (err) {
      handleApiError(err, 'Could not load deliveries');
    } finally {
      setLogLoading(false);
    }
  };

  const onRetry = async (deliveryId: string | number) => {
    if (!logFor) return;
    try {
      await retryDelivery(logFor.id, deliveryId);
      showSuccess({}, 'Requeued', 'Delivery scheduled for retry.');
      await openLog(logFor);
    } catch (err) {
      handleApiError(err, 'Could not retry delivery');
    }
  };

  return (
    <ScrollView style={{ flex: 1, backgroundColor: colors.paper }} contentContainerStyle={{ padding: 24, paddingBottom: 64 }} showsVerticalScrollIndicator={false}>
      <View style={{ flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16, marginBottom: 20 }}>
        <View style={{ flex: 1 }}>
          <Text style={{ fontSize: fontSize['3xl'], fontWeight: fontWeight.semibold, color: colors.ink, letterSpacing: -0.02 * fontSize['3xl'], fontFamily: theme.font.sans }}>Webhooks</Text>
          <Text style={{ fontSize: fontSize.md, color: colors.ink3, marginTop: 4, fontFamily: theme.font.sans }}>
            Keep external mirrors current. Webhooks push catalog changes outward.
          </Text>
        </View>
        <Button kind="accent" icon="plus" onPress={() => setNewOpen(true)}>Add webhook</Button>
      </View>

      <View style={{ maxWidth: 820, width: '100%' }}>
        {loading ? (
          <View style={{ paddingVertical: 48, alignItems: 'center' }}><ActivityIndicator color={colors.accent} /></View>
        ) : hooks.length === 0 ? (
          <Empty title="No webhooks yet">Add an endpoint to receive catalog events.</Empty>
        ) : (
          <View style={{ gap: 12 }}>
            {hooks.map(w => <WebhookCard key={w.id} webhook={w} onToggle={onToggle} onEdit={(x) => setEditTarget(x)} onDelete={onDelete} onRotate={onRotate} onLog={openLog} />)}
          </View>
        )}
      </View>

      <WebhookFormModal
        open={newOpen}
        onClose={() => setNewOpen(false)}
        onSubmit={onCreate}
        busy={busy}
        title="Add webhook"
        submitLabel="Add webhook"
        busyLabel="Adding…"
      />

      <WebhookFormModal
        open={!!editTarget}
        onClose={() => setEditTarget(null)}
        onSubmit={onEditSubmit}
        busy={editBusy}
        title="Edit webhook"
        submitLabel="Save changes"
        busyLabel="Saving…"
        initial={editTarget ? { url: editTarget.url, description: editTarget.description ?? '', eventTypes: editTarget.eventTypes } : undefined}
      />

      <Modal
        open={!!logFor}
        onClose={() => setLogFor(null)}
        size="wide"
        title={logFor ? `Delivery log — ${logFor.url}` : ''}
        footer={<Button onPress={() => setLogFor(null)}>Close</Button>}
      >
        {logLoading ? (
          <View style={{ paddingVertical: 24, alignItems: 'center' }}><ActivityIndicator color={colors.accent} /></View>
        ) : deliveries.length === 0 ? (
          <Empty title="No deliveries yet">Events delivered to this endpoint will appear here.</Empty>
        ) : (
          <View style={{ gap: 6 }}>
            {deliveries.map((d) => (
              <View key={String(d.id)} style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', borderWidth: 1, borderColor: colors.line, borderRadius: 8, padding: 10 }}>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, flex: 1, minWidth: 0 }}>
                  <Chip tone={d.status === 'DELIVERED' ? 'success' : d.status === 'FAILED' || d.status === 'DLQ' ? 'danger' : 'neutral'}>{d.status}</Chip>
                  <Text style={{ fontFamily: theme.font.mono, fontSize: fontSize.xs, color: colors.ink2 }} numberOfLines={1}>
                    {d.eventType} · attempt {d.attempt}{d.responseStatus ? ` · HTTP ${d.responseStatus}` : ''}
                  </Text>
                </View>
                <Button size="xs" icon="refresh" onPress={() => onRetry(d.id)}>Retry</Button>
              </View>
            ))}
          </View>
        )}
      </Modal>

      <ConfirmDialog
        visible={!!delTarget}
        title="Remove webhook?"
        message={delTarget ? `Events will stop being delivered to ${delTarget.url}.` : ''}
        confirmText={deleting ? 'Removing…' : 'Remove'}
        cancelText="Cancel"
        onConfirm={confirmDelete}
        onCancel={() => setDelTarget(null)}
        onClose={() => setDelTarget(null)}
      />
    </ScrollView>
  );
}
